import {
  Connection,
  ConnectionType,
  addConnections,
  deleteConnections,
  setContactsToInviteSent,
  updateConnection,
} from '../../redux/slices/connectionsSlice';
import { store } from '../../redux/store';
import { ProfileField } from '../../types/Profile';
import httpService from '../httpService';
import { CustomFieldResponses } from '../../models/inviteService.model';
import { GenerateFriendlyIdResponse, LocalContactInfo } from '../../models/localContactService.model';
import { RootState } from '../../redux/reducers';

const localContactService = {
  /**
   * Creates external members. Defaults to the selected dashboard; pass `dashboardId` to create them
   * in another dashboard (e.g. a subcommunity dashboard) — in that case the redux connections of the
   * selected dashboard are not touched.
   */
  createLocalContacts: async (contactInfos: LocalContactInfo[], dashboardId?: number): Promise<Connection[]> => {
    if (!contactInfos.length) return Promise.resolve([]);
    const selectedDashboardId = store.getState().application.selectedDashboard;
    const targetDashboardId = dashboardId ?? selectedDashboardId;
    const { data } = await httpService.post<Connection[]>(`/networks/dashboards/${targetDashboardId}/external-members`, contactInfos);
    if (targetDashboardId === selectedDashboardId) store.dispatch(addConnections(data));
    return data;
  },

  /**
   * Links existing (root) external members of the business into a subcommunity dashboard.
   * The linked members keep their data on the root external member in the main dashboard.
   */
  linkExternalMembers: async (dashboardId: number, externalMemberIds: number[]): Promise<Connection[]> => {
    if (!externalMemberIds.length) return Promise.resolve([]);
    const { data } = await httpService.post<Connection[]>(
      `/networks/dashboards/${dashboardId}/external-members/link`,
      { externalMemberIds },
    );
    return data;
  },

  /**
   * Deletes an external member from a specific dashboard (e.g. removes a linked external member
   * from a subcommunity dashboard without touching the root in the main dashboard).
   */
  deleteExternalMemberFromDashboard: async (dashboardId: number, externalMemberId: number): Promise<void> => {
    await httpService.delete(`/networks/dashboards/${dashboardId}/external-members/${externalMemberId}`);
  },

  deleteExternalMembers: async (dashboardMembersIds: number[]): Promise<void> => {
    if (!dashboardMembersIds.length) return;
    const { selectedDashboard } = (store.getState() as RootState).application;
    await httpService.delete(`/networks/dashboards/${selectedDashboard}/members`, {
      data: { dashboardMembersIds },
    });
    store.dispatch(deleteConnections({ dashboardMembersIds }));
  },

  fetchLocalContact: async (localContactId: number): Promise<LocalContactInfo> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    const { data } = await httpService.get(`/networks/dashboards/${selectedDashboard}/external-members/${localContactId}`);
    return data;
  },

  updateLocalContact: async (
    localContactId: number,
    firstName: string,
    lastName: string,
    note: string,
    receivedData: ProfileField[],
    customFieldResponses: CustomFieldResponses,
  ): Promise<void> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    const { data } = await httpService.put(`/networks/dashboards/${selectedDashboard}/external-members/${localContactId}`, {
      firstName,
      lastName,
      note,
      receivedData,
      customFieldResponses,
    });
    store.dispatch(updateConnection(data));
  },

  uploadProfilePicture: async (localContactId: number, image: File): Promise<void> => {
    const formData = new FormData();
    formData.append('avatar', image);
    const { selectedDashboard } = (store.getState() as RootState).application;
    const { data } = await httpService.post(
      `/networks/dashboards/${selectedDashboard}/external-members/${localContactId}/avatar`,
      formData,
    );
    store.dispatch(updateConnection(data));
  },

  getProfilePictureUrl: async (localContactId: number): Promise<string> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService
      .get(`/networks/dashboards/${selectedDashboard}/external-members/${localContactId}/avatar`)
      .then(({ data }): string => {
        return data.path;
      });
  },

  getProfilePictureAsFile: async (localContactId: number): Promise<File> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService
      .get(`/networks/dashboards/${selectedDashboard}/external-members/${localContactId}/avatar`)
      .then(async ({ data }): Promise<File> => {
        const response = await httpService.get<Blob>(data.path, {
          responseType: 'blob',
        });
        return new File([response.data], 'profilePicture');
      });
  },

  deleteProfilePicture: async (localContactId: number): Promise<void> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    await httpService.delete(`/networks/dashboards/${selectedDashboard}/external-members/${localContactId}/avatar`);
  },

  /**
   * Sends invites to external members. Defaults to the selected dashboard; pass `dashboardId` to
   * invite members of another dashboard (e.g. a subcommunity dashboard).
   */
  inviteContacts: async (externalMemberIds: number[], dashboardId?: number): Promise<void> => {
    const { selectedDashboard } = (store.getState() as RootState).application;
    const targetDashboardId = dashboardId ?? selectedDashboard;

    await httpService.post(`/networks/dashboards/${targetDashboardId}/external-members/invite`, { externalMemberIds }).then(() => {
      if (targetDashboardId === selectedDashboard) store.dispatch(setContactsToInviteSent(externalMemberIds));
    });
  },

  generateFriendlyId(localContactId: number): Promise<string> {
    const { selectedDashboard } = (store.getState() as RootState).application;
    return httpService
      .post<GenerateFriendlyIdResponse>(`/networks/dashboards/${selectedDashboard}/external-members/${localContactId}/invite/friendly-id`)
      .then(({ data: { friendlyId } }) => friendlyId);
  },
};

export default localContactService;
